import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';

import { useSendBankAccountMutation } from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { texts } from 'utils/languages/fr';
import { usePaymentMethods, useSubscriptionDocument, useTunnelChangeHandlers } from 'utils/hooks';
import { ribErrors, paymentMethods, docTypes, stepIDs, subscriptionStatus } from 'utils/constants';
import {
    formatIBAN,
    formatIBANOnType,
    checkAcceptedFileTypes,
    getDocumentAvailability,
    isStatusOk,
    convertToBoolOrNull,
    getInvestorErrorMessage,
    validateFileSize,
} from 'utils/helpers';
import { useGetShareSubcategoryQuery } from 'api/main';

import TunnelStep from '../common/TunnelStep';
import TunnelBox from '../common/TunnelBox';
import {
    SubmitButton,
    CustomTextField,
    FileInputButton,
    CustomSelect,
    DocumentLink,
    InfoButton,
    Loader,
    FileErrorMessage,
    RadioLabel,
} from 'components';
import { Box, RadioGroup, Typography, FormLabel, MenuItem } from '@mui/material';

const labelSx = { mb: 1 };
const containerSx = { display: 'flex', flexWrap: 'wrap', flexDirection: 'row', mb: 2 };
const fileSx = { mr: 2, textAlign: 'left', mb: '5px', mt: 2, maxWidth: '550px' };
const inputSx = { maxWidth: '365px' };

const renderOption = opt => (
    <MenuItem key={opt.name} value={opt.value}>
        {opt.name}
    </MenuItem>
);

const invalidIBANErrorKey = 'subscriptions[0].bankingStatement.bankAccountNumber';
const invalidBICErrorKey = 'subscriptions[0].bankingStatement.bankIdentifierCode';
const invalidIBANTitleErrorKey = 'subscriptions[0].bankingStatement.bankAccountNumberTitle';
const bankErrorKey = 'undefined';

const RIBCheck = props => {
    const { handleNextStep, userStep, roleName } = props;

    const mounted = useRef(false);
    const setTunnelChangesHandlers = useTunnelChangeHandlers();

    const [sendBankAccount, { isLoading: buttonLoader, error }] = useSendBankAccountMutation();

    const internalUser = useSelector(userSelectors.getInternalUser);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const externallyManaged = tunnelSubscription?.investmentFund?.externallyManaged;

    const latestFinishedSubscription = useMemo(
        () =>
            internalUser?.subscriptions?.findLast(
                s => s.subscriptionStatus === subscriptionStatus.FINISHED
            ),
        [internalUser]
    );
    const tunnelErrors = useMemo(
        () =>
            error?.data?.errors
                ? Object.fromEntries(error?.data?.errors.map(e => [e.property, e.message]))
                : error?.data?.violations
                ? Object.fromEntries(error?.data?.violations.map(e => [e.propertyPath, e.message]))
                : {},
        [error]
    );

    const isResubmit = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const hasStatusOk = useMemo(() => isStatusOk(userStep), [userStep]);
    const { bankingStatement } = latestFinishedSubscription || tunnelSubscription;
    const { investmentCharacteristics } = tunnelSubscription;
    const subscriberIsPP = roleName.includes('PP');
    const ribDocument = useSubscriptionDocument(
        docTypes.RIB,
        latestFinishedSubscription || tunnelSubscription
    );
    const subscriptionIsTransfered =
        (tunnelSubscription.createdBy && tunnelSubscription.transferredToInvestorAt) ||
        !tunnelSubscription.createdBy;

    const defaultFrenchBased = useMemo(() => {
        if (bankingStatement && 'frenchBased' in bankingStatement) {
            return bankingStatement.frenchBased;
        }
        return '';
    }, [bankingStatement]);

    const defaultState = useMemo(
        () => ({
            frenchBased: defaultFrenchBased,
            bankAccount: formatIBAN(bankingStatement?.bankAccountNumber) || '',
            bankAccountTitle: formatIBAN(bankingStatement?.bankAccountNumberTitle) || '',
            bankIdentifierCode: bankingStatement?.bankIdentifierCode || '',
            paymentMethod: bankingStatement?.paymentMethod || '',
            selectedRIB: ribDocument || null,
            nif: internalUser.investorProfile.nif || '',
            nifReason: internalUser.investorProfile.nifReason || '',
            coSubscriberNif: tunnelSubscription.coSubscriber?.investorProfile?.nif || '',
            coSubscriberNifReason:
                tunnelSubscription.coSubscriber?.investorProfile?.nifReason || '',
            taxExemption:
                typeof internalUser.investorProfile.taxExemption === 'boolean'
                    ? internalUser?.investorProfile?.taxCountry === 'FR' &&
                      internalUser.investorProfile.taxExemption
                    : '',
        }),
        [bankingStatement, defaultFrenchBased, internalUser, ribDocument, tunnelSubscription]
    );

    const { data: shareSubcategory, isLoading: subcategoryLoading } = useGetShareSubcategoryQuery(
        investmentCharacteristics.shareSubcategory?.id,
        { skip: !investmentCharacteristics.shareSubcategory?.id }
    );

    const [bankAccount, setBankAccount] = useState(defaultState.bankAccount);
    const [bankIdentifierCode, setBankIdentifierCode] = useState(defaultState.bankIdentifierCode);
    const [bankAccountTitle, setBankAccountTitle] = useState(defaultState.bankAccountTitle);
    const [paymentMethod, setPaymentMethod] = useState(defaultState.paymentMethod);
    const [disableInput, setDisableInput] = useState(false);
    const [frenchBased, setFrenchBased] = useState(defaultState.frenchBased);
    const [isRIBSelected, setIsRIBSelected] = useState(null);
    const [selectedRIB, setSelectedRIB] = useState(defaultState.selectedRIB);
    const [fileTypeError, setFileTypeError] = useState(false);
    const [fileSizeError, setFileSizeError] = useState(false);
    const [errors, setErrors] = useState({ iban: '' });
    const [form, setForm] = useState({
        nif: defaultState.nif,
        nifReason: defaultState.nifReason,
        coSubscriberNif: defaultState.coSubscriberNif,
        coSubscriberNifReason: defaultState.coSubscriberNifReason,
        taxExemption: defaultState.taxExemption,
    });

    const isDocumentAvailable = useMemo(
        () => getDocumentAvailability(tunnelSubscription?.userTunnelSteps, stepIDs.RIB_CHECK),
        [tunnelSubscription]
    );

    const paymentChoiceOptions = usePaymentMethods({
        subcategoryId: shareSubcategory?.id,
        bankAccount,
    });

    const sharesNumber = useMemo(
        () =>
            (shareSubcategory
                ? investmentCharacteristics?.investingSum / shareSubcategory.value
                : 0
            ).toFixed(tunnelSubscription?.investmentFund?.numberOfDecimals || 3),
        [investmentCharacteristics, shareSubcategory, tunnelSubscription]
    );

    useEffect(() => {
        if (
            paymentChoiceOptions?.length &&
            !paymentChoiceOptions.find(c => c.value === paymentMethod)
        ) {
            setPaymentMethod(paymentChoiceOptions[0].value ?? '');
        }
    }, [paymentChoiceOptions, paymentMethod]);

    useEffect(() => {
        setSelectedRIB(ribDocument);
    }, [ribDocument]);

    useEffect(() => {
        if (!paymentChoiceOptions?.length && shareSubcategory) {
            const { directDebit } = shareSubcategory;

            if (mounted.current) {
                setPaymentMethod(
                    directDebit ? paymentMethods.DIRECT_DEBIT : paymentMethods.CREDIT_TRANSFER
                );
            }
        }
    }, [paymentChoiceOptions, shareSubcategory]);

    const handleIBANChange = useCallback(({ currentTarget }) => {
        const bankAccount = formatIBANOnType(currentTarget);
        setBankAccount(bankAccount);
    }, []);

    const handleBICChange = useCallback(({ target }) => {
        setBankIdentifierCode(target.value ? target.value.toUpperCase().replace(/\s/g, '') : '');
    }, []);

    const handleTitleIBANChange = useCallback(({ currentTarget }) => {
        const bankAccount = formatIBANOnType(currentTarget);
        setBankAccountTitle(bankAccount);
    }, []);

    const handleFormChange = useCallback(
        key =>
            ({ target }) => {
                setForm(prevState => ({
                    ...prevState,
                    [key]: key === 'nif' ? target.value.replace(/\s/gi, '') : target.value,
                }));
            },
        []
    );

    const handleSelect = useCallback(({ target: { value } }) => {
        setPaymentMethod(value);
    }, []);

    const handleRadioChange = useCallback(
        key =>
            ({ currentTarget: { value } }) => {
                if (key === 'frenchBased') {
                    setFrenchBased(convertToBoolOrNull(value));
                } else {
                    setForm(prevState => ({ ...prevState, [key]: value === 'true' }));
                }
            },
        []
    );

    const handleFileChange = useCallback(({ target: { files } }) => {
        const file = files[0];
        checkAcceptedFileTypes(
            file,
            () => {
                if (validateFileSize(file)) {
                    setFileTypeError(false);
                    setSelectedRIB(file);
                    setIsRIBSelected(true);
                    setFileSizeError(false);
                } else {
                    setFileSizeError(true);
                }
            },
            () => {
                setSelectedRIB(null);
                setFileTypeError(true);
                setFileSizeError(false);
            }
        );
    }, []);

    const handleResponse = useCallback(
        afterSubmitCallback => error => {
            if (!error) {
                if (subscriptionIsTransfered) setDisableInput(true);
                afterSubmitCallback?.();
                if (!isResubmit) {
                    handleNextStep();
                }
            } else if (error?.status === 400 || error?.status === 422) {
                const hasInvalidIban = error?.data?.errors?.some(
                    err =>
                        err.property === invalidIBANErrorKey &&
                        (err.message === ribErrors.INVALID_IBAN ||
                            err.message === ribErrors.INVALID_RESPONSE_STATUS ||
                            err.message === ribErrors.INVALID_BIC)
                );

                if (hasInvalidIban) {
                    setErrors(prevState => ({ ...prevState, iban: texts.IBAN_PAS_VALIDE }));
                }
            } else if (error?.status === 415) {
                setFileSizeError(true);
            }
        },
        [handleNextStep, isResubmit, subscriptionIsTransfered]
    );

    const allowSubmit =
        typeof frenchBased === 'boolean' &&
        selectedRIB &&
        bankIdentifierCode &&
        bankAccount &&
        (subscriberIsPP ? form.nif || form.nifReason : true) &&
        (tunnelSubscription.withCoSubscriber
            ? form.coSubscriberNif || form.coSubscriberNifReason
            : true) &&
        (tunnelSubscription.investmentFund?.fiscal &&
        subscriberIsPP &&
        internalUser?.investorProfile?.taxCountry === 'FR'
            ? form.taxExemption !== ''
            : true);

    const validateBankAccount = useCallback(
        afterSubmitCallback => () => {
            setErrors({ iban: '', titleIban: '', bankIdentifierCode: '' });
            let newErrors = {};
            if (externallyManaged && !bankAccountTitle && subscriptionIsTransfered) {
                newErrors.titleIban = texts.SAISIR_IBAN;
            }
            if (!bankAccount && subscriptionIsTransfered) {
                newErrors.iban = texts.SAISIR_IBAN;
            }
            if (!RegExp(/^\w+$/, 'g').test(bankIdentifierCode)) {
                newErrors.bankIdentifierCode = texts.CODE_BIC_INVALID_CHARACTERS;
            }
            if (bankIdentifierCode.length !== 8 && bankIdentifierCode.length !== 11) {
                newErrors.bankIdentifierCode = texts.BIC_FORMAT_CONTENT;
            }

            if (Object.values(newErrors).length > 0) {
                setErrors(prevState => ({ ...prevState, ...newErrors }));
                return;
            }
            const data = {
                bankAccount,
                bankIdentifierCode,
                bankAccountTitle,
                frenchBased,
                paymentMethod,
                selectedRIB: isRIBSelected ? selectedRIB : null,
                ...form,
                taxExemption: subscriberIsPP ? form.taxExemption : undefined,
            };
            sendBankAccount({ payload: data, callback: handleResponse(afterSubmitCallback) });
        },
        [
            sendBankAccount,
            subscriberIsPP,
            bankAccount,
            bankIdentifierCode,
            handleResponse,
            frenchBased,
            paymentMethod,
            subscriptionIsTransfered,
            selectedRIB,
            isRIBSelected,
            form,
            externallyManaged,
            bankAccountTitle,
        ]
    );

    const handleSubmit = useCallback(() => {
        validateBankAccount(() => handleNextStep(true))();
    }, [handleNextStep, validateBankAccount]);

    const checkChanges = useCallback(
        () =>
            !isEqual(
                { ...defaultState },
                {
                    bankAccount,
                    bankAccountTitle,
                    bankIdentifierCode,
                    frenchBased,
                    paymentMethod,
                    selectedRIB,
                    ...form,
                }
            ),
        [
            defaultState,
            bankAccount,
            frenchBased,
            paymentMethod,
            selectedRIB,
            bankAccountTitle,
            bankIdentifierCode,
            form,
        ]
    );

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);

    useEffect(
        () => setTunnelChangesHandlers(validateBankAccount, checkChanges, isResubmit),
        [validateBankAccount, checkChanges, isResubmit, setTunnelChangesHandlers]
    );

    useEffect(() => {
        setForm(prevState => ({
            ...prevState,
            nif: defaultState.nif,
            nifReason: defaultState.nifReason,
            taxExemption: defaultState.taxExemption,
        }));
    }, [defaultState]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    return (
        <TunnelStep
            title={
                <React.Fragment>
                    <Typography component="span" sx={{ display: 'block', fontSize: 'inherit' }}>
                        {subscriberIsPP ? texts.BANK_AND_FISCAL_INFO : texts.COORDONNEES_BANCAIRES}
                    </Typography>
                    {externallyManaged && (
                        <Typography component="span" sx={{ display: 'block', fontSize: 'inherit' }}>
                            ({texts.EXTERNALLY_MANAGED})
                        </Typography>
                    )}
                </React.Fragment>
            }
            subtitle={
                <Typography variant="body1">
                    <i>{texts.DISCONNECT_REMINDER}</i>
                </Typography>
            }
        >
            <TunnelBox
                sx={theme => ({
                    [theme.breakpoints.down(450)]: { paddingLeft: '15px', paddingRight: '15px' },
                })}
            >
                <Loader loading={subcategoryLoading} marginTop={20} height="350px">
                    <FormLabel sx={labelSx} component="legend">
                        {texts.DOMICILIE_EN_FRANCE}
                    </FormLabel>
                    <RadioGroup sx={containerSx} onChange={handleRadioChange('frenchBased')}>
                        <RadioLabel label={texts.OUI} value={true} checked={frenchBased === true} />
                        <RadioLabel
                            label={texts.NON}
                            value={false}
                            checked={frenchBased === false}
                        />
                    </RadioGroup>
                    <Box mt="20px" mb={2}>
                        <FormLabel sx={labelSx} component="legend">
                            {externallyManaged ? texts.SUBMIT_ENTRUSTED_RIB : texts.FOURNIR_LE_RIB}
                        </FormLabel>
                        {fileTypeError && <FileErrorMessage variant="type" />}
                        {fileSizeError && <FileErrorMessage variant="size" />}
                        {!fileSizeError && !fileTypeError && (
                            <Typography sx={fileSx} variant="body2">{`${
                                texts.UPLOADED_FILE_NAME
                            } : ${selectedRIB?.name || texts.AUCUN}`}</Typography>
                        )}
                        <FileInputButton
                            noMargin
                            showIcon
                            id="rib-doc"
                            label={texts.UPLOAD_FILE_BUTTON}
                            onFileChange={handleFileChange}
                        />
                    </Box>
                    {!!ribDocument && (
                        <DocumentLink
                            isDocumentAvailable={isDocumentAvailable}
                            document={ribDocument}
                            label={texts.RIB}
                            align="flex-start"
                            margin="0"
                        />
                    )}
                    <Box display="flex" flexWrap="wrap" alignItems="baseline">
                        <CustomTextField
                            label={
                                (externallyManaged
                                    ? texts.ENTRUSTED_IBAN
                                    : texts.MERCI_SAISIR_IBAN) +
                                ' ' +
                                texts.IBAN_INFO
                            }
                            sx={inputSx}
                            onChange={handleIBANChange}
                            value={bankAccount}
                            error={
                                errors.iban ||
                                getInvestorErrorMessage(tunnelErrors?.[invalidIBANErrorKey]) ||
                                (tunnelErrors?.[bankErrorKey] &&
                                    !tunnelErrors?.[invalidIBANTitleErrorKey])
                            }
                            disabled={disableInput}
                            inputProps={{ maxLength: 42 }}
                        />
                    </Box>
                    <Box display="flex" flexWrap="wrap" alignItems="baseline">
                        <CustomTextField
                            fullWidth
                            label={texts.BIC_LABEL}
                            sx={inputSx}
                            onChange={handleBICChange}
                            value={bankIdentifierCode}
                            error={
                                errors.bankIdentifierCode ||
                                getInvestorErrorMessage(tunnelErrors?.[invalidBICErrorKey]) ||
                                (tunnelErrors?.[bankErrorKey] &&
                                    !tunnelErrors?.[invalidBICErrorKey])
                            }
                            disabled={disableInput}
                            inputProps={{ maxLength: 11 }}
                            infoButton={
                                <InfoButton
                                    disableMarginBottom
                                    title={texts.BIC_FORMAT_TITLE}
                                    content={texts.BIC_FORMAT_CONTENT}
                                />
                            }
                        />
                    </Box>
                    {!!bankingStatement?.bankName && !errors.iban && (
                        <Box mb={4}>
                            <Box
                                textAlign="left"
                                display="flex"
                                flexWrap="wrap"
                                alignItems="baseline"
                                gap={2}
                            >
                                <Typography variant="h6" gutterBottom>
                                    {texts.BANK_NAME}
                                </Typography>
                                <Typography variant="body1" gutterBottom>
                                    {bankingStatement?.bankName}
                                </Typography>
                            </Box>
                            <Box
                                textAlign="left"
                                display="flex"
                                flexWrap="wrap"
                                alignItems="baseline"
                                gap={2}
                            >
                                <Typography variant="h6" gutterBottom>
                                    {texts.CODE_BIC}
                                </Typography>
                                <Typography variant="body1" gutterBottom>
                                    {bankingStatement?.verifiedBankIdentifierCode}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {externallyManaged && (
                        <CustomTextField
                            label={texts.ENTRUSTED_TITLE_IBAN}
                            sx={fileSx}
                            onChange={handleTitleIBANChange}
                            value={bankAccountTitle}
                            error={
                                errors.titleIban ||
                                getInvestorErrorMessage(tunnelErrors?.[invalidIBANTitleErrorKey])
                            }
                            disabled={disableInput}
                            inputProps={{ maxLength: 42 }}
                        />
                    )}
                    {subscriberIsPP && (
                        <React.Fragment>
                            <Box display="flex" alignItems="flex-end">
                                <CustomTextField
                                    fullWidth
                                    sx={inputSx}
                                    label={texts.NIF}
                                    value={form.nif}
                                    onChange={handleFormChange('nif')}
                                    error={getInvestorErrorMessage(
                                        tunnelErrors?.['investorProfile.nif']
                                    )}
                                    infoButton={
                                        <InfoButton
                                            disableMarginBottom
                                            title={texts.NUMERO_ID_FISCAL}
                                            content={texts.NUMERO_FISCAL}
                                        />
                                    }
                                />
                            </Box>
                            <CustomTextField
                                sx={inputSx}
                                label={texts.NIF_ABSENCE_REASON}
                                value={form.nifReason}
                                onChange={handleFormChange('nifReason')}
                            />
                            {tunnelSubscription?.coSubscriber && (
                                <React.Fragment>
                                    <Box display="flex" alignItems="flex-end">
                                        <CustomTextField
                                            sx={inputSx}
                                            label={texts.CO_SUBSCRIBER_NIF}
                                            value={form.coSubscriberNif}
                                            onChange={handleFormChange('coSubscriberNif')}
                                            error={getInvestorErrorMessage(
                                                tunnelErrors?.['investorProfile']
                                            )}
                                            infoButton={
                                                <InfoButton
                                                    disableMarginBottom
                                                    title={texts.NUMERO_ID_FISCAL}
                                                    content={texts.NUMERO_FISCAL}
                                                />
                                            }
                                        />
                                    </Box>
                                    <CustomTextField
                                        sx={inputSx}
                                        label={texts.CO_SUBSCRIBER_NIF_ABSENCE_REASON}
                                        value={form.coSubscriberNifReason}
                                        onChange={handleFormChange('coSubscriberNifReason')}
                                    />
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    )}
                    {tunnelSubscription?.investmentFund?.fiscal &&
                        subscriberIsPP &&
                        internalUser?.investorProfile?.taxCountry === 'FR' && (
                            <React.Fragment>
                                <FormLabel sx={labelSx} component="legend">
                                    {texts.FISCAL_REGIME_LABEL}
                                </FormLabel>
                                <RadioGroup
                                    sx={containerSx}
                                    onChange={handleRadioChange('taxExemption')}
                                    value={form.taxExemption}
                                >
                                    <RadioLabel
                                        label={texts.FISCAL_OPTION_1}
                                        value={false}
                                        checked={form.taxExemption === false}
                                    />
                                    <RadioLabel
                                        label={
                                            <React.Fragment>
                                                <Typography gutterBottom>
                                                    {texts.FISCAL_OPTION_2}
                                                </Typography>
                                                <Typography gutterBottom>
                                                    {texts.FISCAL_OPTION_2_INFO_1(sharesNumber)}
                                                </Typography>
                                                <Typography gutterBottom>
                                                    {texts.FISCAL_OPTION_2_INFO_2}
                                                </Typography>
                                            </React.Fragment>
                                        }
                                        checked={form.taxExemption === true}
                                        value={true}
                                    />
                                </RadioGroup>
                            </React.Fragment>
                        )}
                    <CustomSelect
                        sx={inputSx}
                        label={texts.MODE_REGLEMENT}
                        onChange={handleSelect}
                        value={paymentMethod}
                        error={getInvestorErrorMessage(tunnelErrors?.['paymentMethod'])}
                    >
                        {paymentChoiceOptions?.map(renderOption)}
                    </CustomSelect>
                </Loader>
            </TunnelBox>
            <SubmitButton
                text={isResubmit && changesMade ? texts.SAVE_AND_CONTINUE : texts.SUIVANT}
                onClick={changesMade || !hasStatusOk ? handleSubmit : handleNextStep}
                disabled={
                    buttonLoader ||
                    (!allowSubmit &&
                        !(
                            tunnelSubscription.createdBy &&
                            !tunnelSubscription.transferredToInvestorAt
                        ))
                }
                loading={buttonLoader}
            />
        </TunnelStep>
    );
};

RIBCheck.propTypes = {
    handleNextStep: PropTypes.func,
    userStep: PropTypes.object,
    roleName: PropTypes.string,
};

export default RIBCheck;
